import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  ApiEnvelope,
  CreateIssueRequest,
  CreatedIssue,
  IssueComment,
  IssueAttachmentRef,
  IssueDetail,
  IssueSummary,
  UploadedAttachment
} from '../models/support.models';

@Injectable({ providedIn: 'root' })
export class SupportApi {
  private readonly http = inject(HttpClient);
  private readonly apiOrigin = environment.apiBaseUrl;
  private readonly baseUrl = `${environment.apiBaseUrl}/user/issues`;

  createIssue(payload: CreateIssueRequest): Observable<CreatedIssue> {
    return this.http
      .post<ApiEnvelope<CreatedIssue>>(this.baseUrl, payload)
      .pipe(map((res) => res.data));
  }

  listMyIssues(page = 1, limit = 20): Observable<{ data: IssueSummary[]; total: number; totalPages: number }> {
    const params = new HttpParams().set('page', page).set('limit', limit);
    return this.http
      .get<ApiEnvelope<IssueSummary[]>>(`${this.baseUrl}/my`, { params })
      .pipe(
        map((res) => ({
          data: res.data ?? [],
          total: res.pagination?.total ?? 0,
          totalPages: res.pagination?.totalPages ?? 1
        }))
      );
  }

  getIssue(id: string): Observable<IssueDetail> {
    return this.http
      .get<ApiEnvelope<IssueDetail>>(`${this.baseUrl}/${id}`)
      .pipe(map((res) => this.withAbsoluteAttachmentUrls(res.data)));
  }

  /**
   * The API returns attachment urls RELATIVE to the API host (`/user/issues/...`) —
   * correct, since it shouldn't need to know where it's deployed. But an
   * `<img src="/user/issues/...">` here resolves against the Angular origin, not the
   * API, so the browser fetches index.html, gets HTML, and shows a broken image.
   * Resolve it once, here, rather than in every template.
   *
   * Every url is PINNED to the API origin. Attachments are streamed through an authorized
   * endpoint and are never public URLs, so one pointing elsewhere is not ours — and
   * rendering it would make the user's browser fetch a third-party resource, handing that
   * host their IP and a hit tied to viewing this issue. Off-origin urls are dropped.
   */
  private withAbsoluteAttachmentUrls(detail: IssueDetail): IssueDetail {
    if (!detail?.attachments?.length) return detail;

    const attachments = detail.attachments
      .map((a) => {
        const url = this.resolveApiUrl(a.url);
        return url ? { ...a, url } : null;
      })
      .filter((a): a is IssueAttachmentRef => a !== null);

    return { ...detail, attachments };
  }

  private resolveApiUrl(raw: string): string | null {
    if (!raw) return null;
    try {
      const base = new URL(this.apiOrigin, window.location.origin);
      const resolved = new URL(raw, base);
      return resolved.origin === base.origin ? resolved.toString() : null;
    } catch {
      return null; // unparseable — treat exactly like off-origin
    }
  }

  /**
   * Step 1 of the two-step attachment flow: upload the file, get an id back. The id is
   * then submitted in `attachments[]` on createIssue().
   *
   * Deliberately does NOT set Content-Type — the browser must set it itself so the
   * multipart boundary is included. Setting it by hand produces a body the server
   * cannot parse. The auth interceptor adds credentials and the CSRF token.
   */
  uploadAttachment(file: File): Observable<UploadedAttachment> {
    const form = new FormData();
    form.append('file', file);
    return this.http
      .post<ApiEnvelope<UploadedAttachment>>(`${this.baseUrl}/attachments`, form)
      .pipe(map((res) => res.data));
  }

  /**
   * Fetch an attachment's bytes through HttpClient. Attachments are streamed by an AUTHORIZED
   * endpoint, and a raw `<img src>` to it carries no credentials cross-origin → 401 → broken image.
   * Going through HttpClient lets the auth interceptor attach credentials + CSRF, so the caller can
   * render the result via an object URL. `url` is already pinned to the API origin by getIssue().
   */
  fetchAttachmentBlob(url: string): Observable<Blob> {
    return this.http.get(url, { responseType: 'blob' });
  }

  addComment(id: string, body: string): Observable<IssueComment> {
    return this.http
      .post<ApiEnvelope<IssueComment>>(`${this.baseUrl}/${id}/comments`, { body })
      .pipe(map((res) => res.data));
  }

  reopenIssue(id: string, comment: string): Observable<IssueSummary> {
    return this.http
      .post<ApiEnvelope<IssueSummary>>(`${this.baseUrl}/${id}/reopen`, { comment })
      .pipe(map((res) => res.data));
  }
}
